import React from 'react';
import styled from '@emotion/styled';
import { Icon } from '../icons';

type Props = {
  icon: string,
  customIcon?: string,
  fill?: string,
  title: string,
  description: string,
  dataTest?: string,
};

export const ProgressStep = ({ icon, customIcon, fill, title, description, dataTest = 'progress-step' }: Props) => (
  <ProgressElement>
    <ProgressIcon noMargin={!title}>{customIcon || <IconWrapper fill={fill} name={icon} />}</ProgressIcon>
    <Title noMargin={!title}>
      {title}
      <Description data-test={`${dataTest}-description`}>{description}</Description>
    </Title>
  </ProgressElement>
);

const IconWrapper = styled(Icon)`
  fill: ${({ theme, fill }) => fill || theme.color.brand};
  width: 48px;
  height: 48px;

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    width: 20px;
    height: 20px;
  }
`;

const ProgressIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background-color: ${({ theme }) => theme.color.white};
  border: ${({ theme }) => theme.stroke.light};

  &:after {
    position: absolute;
    content: '';
    border-bottom: ${({ theme }) => theme.stroke.dashed};
    width: 100%;
    left: -50%;
    z-index: -1;
  }

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    width: 48px;
    height: 48px;
    flex: none;
    margin-right: 16px;
    font-size: ${({ theme }) => theme.fontSize.xl};

    &:after {
      width: auto;
      height: 100%;
      left: auto;
      top: ${({ noMargin }) => (noMargin ? '-44px' : '-80px')};
      border-right: ${({ theme }) => theme.stroke.dashed};
    }
  }
`;

const ProgressElement = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex-grow: 1;
  flex-basis: 0;
  padding: 0 16px;

  &:first-of-type {
    padding-left: 0;
    ${ProgressIcon}:after {
      display: none;
    }
  }
  &:last-of-type {
    padding-right: 0;
  }

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    padding: 0 0 45px 0;
    align-items: flex-start;
    text-align: left;
    flex-direction: row;

    &:last-child {
      padding-bottom: 0;
    }
  }
`;

const Title = styled('h3')`
  margin: ${({ noMargin }) => (noMargin ? 0 : '16px')} 0 0 0;
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.color.black};

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    font-size: ${({ theme }) => theme.fontSize.lg};
  }
`;

const Description = styled('span')`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.grey};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  display: block;
  margin-top: 16px;

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    font-size: ${({ theme }) => theme.fontSize.sm};
    margin-top: 8px;
  }
`;

ProgressStep.defaultProps = {
  title: null,
};
